import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AnalyticsService } from '../../services/analytics';

export interface JwtDecoderState {
  inputToken: string;
  decodedHeader: string;
  decodedPayload: string;
  isDecoding: boolean;
  error: string | null;
}

const initialState: JwtDecoderState = {
  inputToken: '',
  decodedHeader: '',
  decodedPayload: '',
  isDecoding: false,
  error: null,
};

export const decodeJwtAsync = createAsyncThunk(
  'jwtDecoder/decodeJwtAsync',
  async (token: string, { rejectWithValue }) => {
    try {
                   // Note: Analytics service would be injected here in a real implementation
             // For now, we'll just log the event
             console.log('JWT Decoder: decode event', 'jwt_token');
      
      // Split the JWT token into parts
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid JWT token format. JWT tokens should have 3 parts separated by dots.');
      }

      const [header, payload] = parts;
      
      // Decode header and payload (they are base64url encoded)
      const decodedHeader = JSON.stringify(JSON.parse(atob(header.replace(/-/g, '+').replace(/_/g, '/'))), null, 2);
      const decodedPayload = JSON.stringify(JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))), null, 2);
      
      return {
        header: decodedHeader,
        payload: decodedPayload,
      };
    } catch (error) {
                   // Note: Analytics service would be injected here in a real implementation
             // For now, we'll just log the event
             console.log('JWT Decoder: error event', 'decode_failed');
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to decode JWT token');
    }
  }
);

const jwtDecoderSlice = createSlice({
  name: 'jwtDecoder',
  initialState,
  reducers: {
    inputTokenChanged: (state, action: PayloadAction<string>) => {
      state.inputToken = action.payload;
      state.error = null;
    },
    clearDecoded: (state) => {
      state.decodedHeader = '';
      state.decodedPayload = '';
      state.error = null;
    },
    clearAll: (state) => {
      state.inputToken = '';
      state.decodedHeader = '';
      state.decodedPayload = '';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(decodeJwtAsync.pending, (state) => {
        state.isDecoding = true;
        state.error = null;
      })
      .addCase(decodeJwtAsync.fulfilled, (state, action) => {
        state.isDecoding = false;
        state.decodedHeader = action.payload.header;
        state.decodedPayload = action.payload.payload;
        state.error = null;
      })
      .addCase(decodeJwtAsync.rejected, (state, action) => {
        state.isDecoding = false;
        state.error = action.payload as string;
        state.decodedHeader = '';
        state.decodedPayload = '';
      });
  },
});

export const { inputTokenChanged, clearDecoded, clearAll } = jwtDecoderSlice.actions;
export default jwtDecoderSlice.reducer;
